import { gql } from '@apollo/client';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { VisitorModel } from 'client/data/models/visitor';
import { withMetrics } from 'client/data/api/api-metrics';
import { EdmundsGraphQLFederation } from 'client/data/graphql/graphql-client';
import { PAGE_CAR_TYPES, CAR_TYPES } from 'site-modules/shared/constants/upper-funnel/car-types';
import { logger } from 'client/utils/logger/logger';
import { GEM_DEAL_THRESHOLD } from 'site-modules/shared/constants/lease-deals/lease-deals';

// Mapping type to edmundsTypeCategories in client/site-modules/shared/constants/upper-funnel/type-rankings-gql-queries.js
const edmundsTypeCategoriesMap = {
  [PAGE_CAR_TYPES.COMPACT]: ['Sedan', 'Hatchback', 'Wagon', 'SUV'],
  [PAGE_CAR_TYPES.CONVERTIBLE]: ['Convertible'],
  [PAGE_CAR_TYPES.COUPE]: ['Coupe'],
  [PAGE_CAR_TYPES.CROSSOVER]: ['SUV'],
  [PAGE_CAR_TYPES.ECONOMY]: ['Sedan', 'Hatchback'],
  [CAR_TYPES.ELECTRIC]: ['Electric'],
  [PAGE_CAR_TYPES.EXOTIC]: ['Coupe', 'Sedan', 'Convertible'],
  [PAGE_CAR_TYPES.HATCHBACK]: ['Hatchback'],
  [PAGE_CAR_TYPES.HYBRID]: ['Hybrid'],
  [PAGE_CAR_TYPES.LUXURY]: ['Sedan', 'Coupe', 'SUV', 'Hybrid'],
  [PAGE_CAR_TYPES.MIDSIZE]: ['Sedan', 'Wagon', 'SUV'],
  [PAGE_CAR_TYPES.MINIVAN]: ['Minivan'],
  [PAGE_CAR_TYPES.MUSCLE]: ['Coupe'],
  [PAGE_CAR_TYPES.SEDAN]: ['Sedan'],
  [PAGE_CAR_TYPES.SPORTS_CAR]: ['Coupe'],
  [PAGE_CAR_TYPES.SUBCOMPACT]: ['Sedan', 'Hatchback', 'SUV'],
  [PAGE_CAR_TYPES.SUV]: ['SUV'],
  [PAGE_CAR_TYPES.TRUCK]: ['Truck'],
  [PAGE_CAR_TYPES.WAGON]: ['Wagon'],
};

const transformLeaseDeals = deals =>
  deals?.map(({ deal }) => {
    const modelYear = deal?.modelYear;
    const activeListingStats = deal?.activeListingStats;
    const payment = deal?.payment;

    return {
      id: deal?.id,
      payment,
      makeNiceName: modelYear?.makeSlug,
      modelNiceName: modelYear?.modelSlug,
      year: modelYear?.year,
      paymentMedian: activeListingStats?.paymentMedian,
    };
  });

const getTypeLeaseDealsTypeCategoryPath = type => `leaseDeals["${type}"]`;

export const TypeLeaseDealsPaths = {
  getTypeLeaseDealsTypeCategoryPath,
};

export const TypeLeaseDealsModel = createModelSegment('typeLeaseDeals', [
  {
    /**
     * This path is used for the entry point (link) component on the type page, which redirects a user to hidden gems lease deals page
     * @see getTypeLeaseDealsTypeCategoryPath
     */
    path: 'leaseDeals["{type}"]',
    async resolve(match, context) {
      try {
        const { type } = match;
        const { zipCode } = await context.resolveValue('location', VisitorModel);

        if (!zipCode || !edmundsTypeCategoriesMap[type]?.length) return { deals: [] };

        const queryFilters = {
          leaseDealFilters: {
            zip: zipCode,
            edmundsTypeCategories: { in: edmundsTypeCategoriesMap[type] },
            effectivePaymentRatio: { gte: GEM_DEAL_THRESHOLD },
          },
          page: {
            pageNum: 1,
            pageSize: 40,
          },
          sorts: [{ by: 'EFFECTIVE_PAYMENT_RATIO', order: 'DESC' }],
        };

        const graphqlResponse = await withMetrics(EdmundsGraphQLFederation, context).query(
          gql`
            query ($leaseDealFilters: LeaseDealFilters!, $page: PageRequest!, $sorts: [LeaseDealSort!]) {
              leaseDeals(leaseDealFilters: $leaseDealFilters) {
                aggregation {
                  topDealsByModel(page: $page, sorts: $sorts) {
                    deal {
                      id
                      modelYear {
                        makeName
                        modelName
                        makeSlug
                        modelSlug
                        year
                      }
                      payment
                      activeListingStats {
                        paymentMedian
                      }
                    }
                  }
                }
              }
            }
          `,
          queryFilters
        );

        return { deals: transformLeaseDeals(graphqlResponse?.leaseDeals?.aggregation?.topDealsByModel) };
      } catch (error) {
        logger.error('Failed to load data', error);
        return { deals: [] };
      }
    },
  },
]);
